import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
    Typography,
    Paper,
    TableContainer,
    Tooltip,
    TablePagination,
    IconButton,
    Box,
    TextField,
    FormControl,
    InputLabel,
    Stack,
    Checkbox,
    Select,
    MenuItem,
    Grid
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import moment from 'moment';
import axiosInstance from '../../../lib/axios';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import UpdateIcon from '@mui/icons-material/Update';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import dayjs from 'dayjs';

const BulkUpdateEnquiryDialog = ({ open, onClose, propId, selectedFollowBy, statusFilter, setOpenBulkUpdate }) => {
    console.log('propId', propId)
    const [enquiryRows, setEnquiryRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [totalCount, setTotalCount] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [fromDate, setFromDate] = useState(dayjs().subtract(3, 'months'));
    const [toDate, setToDate] = useState(dayjs());
    const [hasSearched, setHasSearched] = useState(false);
    const [columnSearchTerms, setColumnSearchTerms] = useState({});
    const [selectedRows, setSelectedRows] = useState({});
    const [statusUpdateValue, setStatusUpdateValue] = useState('');
    const [remarkUpdateValue, setRemarkUpdateValue] = useState('');
    const [showUpdateControls, setShowUpdateControls] = useState(false);
    const isMounted = React.useRef(true);


    const statusOptions = [
        { value: "0", label: "Cancel" },
        { value: "1", label: "Open" },
        { value: "5", label: "Close" }
    ];

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);


    useEffect(() => {
        if (open && propId) {
            setFromDate(dayjs().subtract(3, 'months'));
            setToDate(dayjs());
            setHasSearched(false);
            setEnquiryRows([]);
            setFilteredRows([]);
            setTotalCount(0);
            setColumnSearchTerms({});
            setSelectedRows({});
            setShowUpdateControls(false);
            setStatusUpdateValue('');
            setRemarkUpdateValue('');
        }
    }, [open, propId]);

    useEffect(() => {
        applyColumnFilters();
    }, [enquiryRows, columnSearchTerms]);

    const applyColumnFilters = () => {
        let filtered = [...enquiryRows];

        Object.keys(columnSearchTerms).forEach(columnId => {
            const searchTerm = columnSearchTerms[columnId];
            if (searchTerm && searchTerm.trim() !== '') {
                filtered = filtered.filter(row => {
                    const value = row[columnId];
                    if (value === null || value === undefined) return false;
                    return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
                });
            }
        });

        setFilteredRows(filtered);
        setTotalCount(filtered.length);
        setPage(1);
    };

    const fetchEnquiryList = async () => {
        setLoading(true);
        try {
            const payload = {
                PageNumber: 1,
                PageSize: 10000,
                SearchText: "",
                Flag: "0",
                PropId: propId?.toString() || "",
                FlwUserId: 1,
                EnqDt: fromDate ? fromDate.format('YYYY-MM-DDTHH:mm:ss') : null,
                LastFlwDt: toDate ? toDate.format('YYYY-MM-DDTHH:mm:ss') : null
            };

            const response = await axiosInstance.post(
                `TrnEnq/GetTrnEnqReport`,
                payload
            );

            if (response.data.Status === 0 && response.data.Data) {
                const formattedData = response.data.Data.map(item => ({
                    ...item,
                    EnqDt: item.EnqDt ? moment(item.EnqDt).format('DD-MM-YYYY') : '',
                    NextFlwDt: item.NextFlwDt ? moment(item.NextFlwDt).format('DD-MM-YYYY') : '',
                    FlwDt: item.FlwDt ? moment(item.FlwDt).format('DD-MM-YYYY') : '',
                    LastFlwDt: item.LastFlwDt ? moment(item.LastFlwDt).format('DD-MM-YYYY') : '',
                    CheckInDt: item.CheckInDt ? moment(item.CheckInDt).format('DD-MM-YYYY') : '',
                    VisitDt: item.VisitDt ? moment(item.VisitDt).format('DD-MM-YYYY') : '',
                    OtherRmk: item.OtherRmk,
                    VisitSt: item.VisitSt === 'Y' ? 'Yes' : item.VisitSt === 'N' ? 'No' : '',
                    Status: item.Status === "0" ? 'Cancel' :
                        item.Status === "1" ? 'Open' :
                            item.Status === "5" ? 'Close' :
                                item.Status || 'Unknown',
                    StatusValue: item.Status || "1"
                }));
                setEnquiryRows(formattedData);
                setFilteredRows(formattedData);
                setTotalCount(formattedData.length);
            } else {
                setEnquiryRows([]);
                setFilteredRows([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Error fetching enquiries:', error);
            setEnquiryRows([]);
            setFilteredRows([]);
            setTotalCount(0);
            toast.error('Failed to fetch enquiries');
        } finally {
            setLoading(false);
        }
    };

    const handleGetData = () => {
        if (!fromDate || !toDate) {
            toast.warning('Please select both from date and to date');
            return;
        }
        if (fromDate.isAfter(toDate)) {
            toast.warning('From date cannot be after to date');
            return;
        }
        setHasSearched(true);
        setColumnSearchTerms({});
        setSelectedRows({});
        setShowUpdateControls(false);
        fetchEnquiryList();
    };

    const handleReset = () => {
        setSearchText('');
        setFromDate(dayjs().subtract(3, 'months'));
        setToDate(dayjs());
        setPage(1);
        setHasSearched(false);
        setEnquiryRows([]);
        setFilteredRows([]);
        setTotalCount(0);
        setColumnSearchTerms({});
        setSelectedRows({});
        setShowUpdateControls(false);
        setStatusUpdateValue('');
        setRemarkUpdateValue('');
    };


    const handleRowSelect = (enqId) => {
        setSelectedRows(prev => ({
            ...prev,
            [enqId]: !prev[enqId]
        }));
    };


    const handleSelectAll = () => {
        const currentPageIds = getCurrentPageRows().map(row => row.EnqId);
        const allSelected = currentPageIds.every(id => selectedRows[id]);

        if (allSelected) {

            const newSelectedRows = { ...selectedRows };
            currentPageIds.forEach(id => {
                delete newSelectedRows[id];
            });
            setSelectedRows(newSelectedRows);
        } else {

            const newSelectedRows = { ...selectedRows };
            currentPageIds.forEach(id => {
                newSelectedRows[id] = true;
            });
            setSelectedRows(newSelectedRows);
        }
    };


    const handleSelectAllPages = () => {
        const allIds = filteredRows.map(row => row.EnqId);
        const allSelected = allIds.every(id => selectedRows[id]);

        if (allSelected) {
            setSelectedRows({});
        } else {
            const newSelectedRows = {};
            allIds.forEach(id => {
                newSelectedRows[id] = true;
            });
            setSelectedRows(newSelectedRows);
        }
    };


    const getSelectedCount = () => {
        return Object.keys(selectedRows).filter(key => selectedRows[key]).length;
    };


    const handleBulkUpdate = async () => {
        const selectedCount = getSelectedCount();

        if (selectedCount === 0) {
            toast.warning('Please select at least one enquiry to update');
            return;
        }

        if (!statusUpdateValue) {
            toast.warning('Please select a status to update');
            return;
        }

        if (!remarkUpdateValue || remarkUpdateValue.trim() === '') {
            toast.warning('Please enter a remark');
            return;
        }

        const updatePayload = Object.keys(selectedRows)
            .filter(enqId => selectedRows[enqId])
            .map(enqId => ({
                EnqId: parseInt(enqId),
                Flag: "BulkStatus",
                Status: statusUpdateValue,
                Remark: remarkUpdateValue
            }));

        if (updatePayload.length === 0) {
            toast.warning('No enquiries selected for update');
            return;
        }

        setUpdating(true);
        try {
            const response = await axiosInstance.post(
                `TrnEnq/TrnEnqStatus_BulkUpdate`,
                updatePayload
            );

            if (response.data.Status === 0) {
                toast.success(`Successfully updated ${updatePayload.length} enquiry(s)`);

                await fetchEnquiryList();

                setSelectedRows({});
                setShowUpdateControls(false);
                setStatusUpdateValue('');
                setRemarkUpdateValue('');
            } else {
                toast.error(response.data.Message || 'Failed to update enquiries');
            }
        } catch (error) {
            console.error('Error updating enquiries:', error);
            toast.error('Failed to update enquiries. Please try again.');
        } finally {
            setUpdating(false);
        }
    };

    const handleColumnSearchChange = (columnId, value) => {
        setColumnSearchTerms(prev => ({
            ...prev,
            [columnId]: value
        }));
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage + 1);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };


    const getCurrentPageRows = () => {
        const start = (page - 1) * rowsPerPage;
        const end = start + rowsPerPage;
        return filteredRows.slice(start, end);
    };

    const columns = [
        { id: 'FlwEmpNm', label: 'Follow By', width: 85 },
        { id: 'EnqNo', label: 'Enquiry No', width: 85 },
        { id: 'EnqDt', label: 'EnqDate', width: 90 },
        { id: 'LastFlwDt', label: 'LastFollowUp', width: 100 },
        { id: 'Status', label: 'Status', width: 50 },
        { id: 'EnqTypeNm', label: 'EnqType', width: 60 },
        { id: 'VisitSt', label: 'Visit', width: 50 },
        { id: 'Remark', label: 'Remark', width: 130 },
        { id: 'NextFlwDt', label: 'NextFollowUp', width: 100 },
        { id: 'EnqBy', label: 'EnqBy', width: 90 },
        { id: 'EnqFor', label: 'EnqFor', width: 90 },
        { id: 'MobileNo', label: 'Mobile', width: 90 },
        { id: 'OcupntMob', label: 'OcupntMob', width: 100 },
        { id: 'Tenure', label: 'Tenure', width: 60 },
        { id: 'SourceBy', label: 'Source By', width: 80 },
        { id: 'SourceName', label: 'Source', width: 85 },
        { id: 'VisitEmpNm', label: 'VisitWith', width: 100 },
        { id: 'VisitRemark', label: 'VisitRemark', width: 150 },
        { id: 'BrokerName', label: 'Broker', width: 100 },
        { id: 'EmailId', label: 'EmailId', width: 130 },
        { id: 'CheckInDt', label: 'CheckIn Date', width: 90 },
        { id: 'RoomTypeName', label: 'RoomType', width: 85 },
        { id: 'VisitDt', label: 'Visit Date', width: 90 },
        { id: 'SemesterName', label: 'Semester', width: 70 },
        { id: 'CourseName', label: 'Course', width: 100 },
        { id: 'DesiName', label: 'Designation', width: 100 }
    ];

    const getCellStyle = (columnId, value) => {
        if (columnId === 'Status') {
            switch (value) {
                case 'Cancel': return { backgroundColor: '#F44336', color: 'white', borderRadius: '4px', textAlign: 'center', padding: '0px 4px' };
                case 'Close': return { backgroundColor: '#4CAF50', color: 'white', borderRadius: '4px', textAlign: 'center', padding: '0px 4px' };
                case 'Open': return { backgroundColor: '#2196F3', color: 'white', borderRadius: '4px', textAlign: 'center', padding: '0px 4px' };
                default: return { textAlign: 'center' };
            }
        }
        if (columnId === 'EnqTypeNm') {
            switch (value) {
                case 'Hot': return { backgroundColor: '#FF0000', color: 'white', borderRadius: '4px', padding: '2px 4px' };
                case 'Warm': return { backgroundColor: '#FFBF00', color: 'white', borderRadius: '4px', padding: '2px 4px' };
                case 'Booked': return { backgroundColor: '#01bc5f', color: 'white', borderRadius: '4px', padding: '2px 4px' };
                case 'Refund': return { backgroundColor: '#4d7499', color: 'white', borderRadius: '4px', padding: '2px 4px' };
                case 'Not interested': return { backgroundColor: '#FF6347', color: 'white', borderRadius: '4px', padding: '2px 4px' };
                case 'Incorrect Lead': return { backgroundColor: '#922b21', color: 'white', borderRadius: '4px', padding: '2px 4px' };
                case 'Cold': return { backgroundColor: '#0000FF', color: 'white', borderRadius: '4px', padding: '2px 4px' };
                case 'Low Budget': return { backgroundColor: '#148f77', color: 'white', borderRadius: '4px', padding: '2px 4px' };
                case 'Others': return { backgroundColor: '#FFA500', color: 'white', borderRadius: '4px', padding: '2px 4px' };
                default: return {};
            }
        }
        if (columnId === 'VisitSt') {
            return value === 'Yes'
                ? { backgroundColor: '#4CAF50', color: 'white', borderRadius: '4px', textAlign: 'center', padding: '2px 4px' }
                : value === 'No' ? { backgroundColor: '#F44336', color: 'white', borderRadius: '4px', textAlign: 'center', padding: '2px 4px' }
                    : { textAlign: 'center' };
        }
        return {};
    };

    const currentPageRows = getCurrentPageRows();
    const selectedCount = getSelectedCount();

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        maxHeight: '90vh',
                    }
                }}
            >
                <ToastContainer
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss={false}
                    draggable
                    pauseOnHover
                    limit={3}
                />
                <DialogTitle
                    sx={{
                        backgroundColor: '#635bff',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 16px',
                        borderBottom: '1px solid #e0e0e0'
                    }}
                >
                    <Typography variant="h6" component="span" sx={{ fontSize: '1.1rem' }}>
                        Enquiry Report - Bulk Update
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)'
                            }
                        }}
                    >
                        <CloseIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{
                        padding: 1,
                        backgroundColor: '#f5f5f5',
                        borderBottom: '1px solid #e0e0e0',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        alignItems: 'flex-end'
                    }}>
                        <DatePicker
                            label="From Date"
                            value={fromDate}
                            onChange={(newValue) => setFromDate(newValue)}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    sx: { minWidth: 180, flex: 1 }
                                }
                            }}
                        />

                        <DatePicker
                            label="To Date"
                            value={toDate}
                            onChange={(newValue) => setToDate(newValue)}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    sx: { minWidth: 180, flex: 1 }
                                }
                            }}
                        />

                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleGetData}
                                size="medium"
                            >
                                Get Data
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleReset}
                                size="medium"
                            >
                                Reset
                            </Button>
                        </Stack>
                    </Box>

                    {selectedCount > 0 && (
                        <Box sx={{
                            padding: 1,
                            backgroundColor: '#e3f2fd',
                            borderBottom: '1px solid #90caf9',
                        }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', }}>
                                        {selectedCount} item(s) selected
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={2}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>Update Status</InputLabel>
                                        <Select
                                            value={statusUpdateValue}
                                            onChange={(e) => setStatusUpdateValue(e.target.value)}
                                            label="Update Status"
                                        >
                                            {statusOptions.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={7}>
                                    <TextField
                                        size="small"
                                        label="Remark"
                                        placeholder="Enter remark for selected items"
                                        value={remarkUpdateValue}
                                        onChange={(e) => setRemarkUpdateValue(e.target.value)}
                                        fullWidth
                                        multiline
                                        rows={1}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            startIcon={updating ? <CircularProgress size={20} color="inherit" /> : <UpdateIcon />}
                                            onClick={handleBulkUpdate}
                                            disabled={updating || !statusUpdateValue || !remarkUpdateValue}
                                            fullWidth
                                        >
                                            Update Selected
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                setSelectedRows({});
                                                setShowUpdateControls(false);
                                                setStatusUpdateValue('');
                                                setRemarkUpdateValue('');
                                            }}
                                        >
                                            Clear
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                            <CircularProgress size={40} />
                        </div>
                    ) : filteredRows.length > 0 ? (
                        <>
                            <TableContainer sx={{ maxHeight: 'calc(90vh - 200px)' }}>
                                <Table stickyHeader size="small" sx={{ minWidth: 650 }}>
                                    <TableHead>
                                        <TableRow sx={{ height: 'auto' }}>
                                        
                                            <TableCell
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#dddbff',
                                                    whiteSpace: 'nowrap',
                                                    borderRight: '1px solid #e0e0e0',
                                                    padding: '4px 6px',
                                                    width: '40px',
                                                    minWidth: '40px',
                                                    verticalAlign: 'top'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                                                    <Checkbox
                                                        checked={currentPageRows.length > 0 && currentPageRows.every(row => selectedRows[row.EnqId])}
                                                        indeterminate={currentPageRows.some(row => selectedRows[row.EnqId]) && !currentPageRows.every(row => selectedRows[row.EnqId])}
                                                        onChange={handleSelectAll}
                                                        size="small"
                                                    />

                                                </Box>
                                            </TableCell>

                                            {columns.map(column => (
                                                <TableCell
                                                    key={column.id}
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        backgroundColor: '#dddbff',
                                                        whiteSpace: 'nowrap',
                                                        borderRight: '1px solid #e0e0e0',
                                                        padding: '4px 6px',
                                                        width: column.width,
                                                        minWidth: column.width,
                                                        verticalAlign: 'top'
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                                                            {column.label}
                                                        </Typography>
                                                        <TextField
                                                            size="small"
                                                            placeholder="Search..."
                                                            value={columnSearchTerms[column.id] || ''}
                                                            onChange={(e) => handleColumnSearchChange(column.id, e.target.value)}
                                                            sx={{
                                                                width: '100%',
                                                                '& .MuiOutlinedInput-root': {
                                                                    fontSize: '0.7rem',
                                                                    height: '24px',
                                                                    padding: '0 4px'
                                                                },
                                                                '& .MuiOutlinedInput-input': {
                                                                    padding: '4px 6px',
                                                                    fontSize: '0.7rem'
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {currentPageRows.map((row) => (
                                            <TableRow
                                                hover
                                                key={row.EnqId}
                                                sx={{
                                                    '&:hover': {
                                                        backgroundColor: '#f5f5f5'
                                                    },
                                                    height: '28px',
                                                    backgroundColor: selectedRows[row.EnqId] ? '#e3f2fd' : 'inherit'
                                                }}
                                            >
                                               
                                                <TableCell
                                                    sx={{
                                                        padding: '1px 6px',
                                                        borderRight: '1px solid #e0e0e0',
                                                        width: '40px',
                                                        minWidth: '40px',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={selectedRows[row.EnqId] || false}
                                                        onChange={() => handleRowSelect(row.EnqId)}
                                                        size="small"
                                                    />
                                                </TableCell>

                                                {columns.map(column => {
                                                    const value = row[column.id];
                                                    return (
                                                        <TableCell
                                                            key={column.id}
                                                            sx={{
                                                                padding: '1px 6px',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                borderRight: '1px solid #e0e0e0',
                                                                maxWidth: column.width,
                                                                minWidth: column.width,
                                                                fontSize: '0.7rem',
                                                                height: '23px',
                                                                lineHeight: '1',
                                                                ...getCellStyle(column.id, value)
                                                            }}
                                                        >
                                                            <Tooltip
                                                                title={value || ''}
                                                                arrow
                                                                placement="top"
                                                                componentsProps={{
                                                                    tooltip: {
                                                                        sx: {
                                                                            fontSize: '12px',
                                                                            maxWidth: '500px'
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                <span style={{ fontSize: '0.7rem',padding:'0px' }}>{value || '-'}</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '6px 16px',
                                borderTop: '1px solid #e0e0e0',
                                backgroundColor: '#f5f5f5',
                                minHeight: '48px'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                                        Showing {filteredRows.length > 0 ? (page - 1) * rowsPerPage + 1 : 0} - {Math.min(page * rowsPerPage, filteredRows.length)} of {filteredRows.length}
                                    </Typography>

                                    {selectedCount > 0 && (
                                        <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#1976d2', fontWeight: 'bold' }}>
                                            {selectedCount} item(s) selected
                                        </Typography>
                                    )}

                                    <TablePagination
                                        rowsPerPageOptions={[15, 50, 100, 500]}
                                        component="div"
                                        count={filteredRows.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page - 1}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        labelDisplayedRows={() => null}
                                        sx={{
                                            '& .MuiTablePagination-toolbar': {
                                                minHeight: '30px',
                                                padding: '0 8px'
                                            },
                                            '& .MuiSelect-select': {
                                                fontSize: '0.9rem',
                                                padding: '0px 8px'
                                            },
                                            '& .MuiTablePagination-selectLabel': {
                                                fontSize: '0.9rem'
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                        </>
                    ) : hasSearched ? (
                        <Typography variant="body1" align="center" sx={{ padding: '40px', fontSize: '0.85rem' }}>
                            No enquiries found
                        </Typography>
                    ) : (
                        <Typography variant="body1" align="center" sx={{ padding: '40px', fontSize: '0.85rem', color: '#666' }}>
                            Please select date range and click "Get Data" button to view enquiries
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>
        </LocalizationProvider>
    );
};

export default BulkUpdateEnquiryDialog;